import { StatsAdapterInterface } from '../../../stats/adapter.interface';
import { StatsContextInterface } from '../../../stats/context.interface';
import { GtmStatsDataEventCtaClickInterface } from '../data/event-cta-click.interface';
import { GtmStatsAdapterList } from './list';
import { DataKeyValueStringInterface } from '../../../data/key-value/string.interface';

export class GtmStatsAdapterEventCtaClick implements StatsAdapterInterface {
  /**
   * Mapping of CtaDownloadType to event action
   */
  protected mappingDownload: DataKeyValueStringInterface = {
    app: 'Download:App',
    'app-android': 'Download:Android App',
    'app-ios': 'Download:iOS App',
  };

  /**
   * Mapping of CtaClientType to event action
   */
  protected mappingClient: DataKeyValueStringInterface = {
    login: 'Login:Client',
    register: 'Register:Company',
  };

  /**
   * Mapping of CtaFilterPropertyType to event action
   */
  protected mappingFilterProperty: DataKeyValueStringInterface = {
    0: 'Filter Listings:All',
    1: 'Filter Listings:For sale',
    2: 'Filter Listings:For rent',
    3: 'Filter Listings:Commercial sale',
    4: 'Filter Listings:Commercial rent',
  };

  /**
   * Mapping of CtaSortPropertyType to event action
   */
  protected mappingSortProperty: DataKeyValueStringInterface = {
    mr: 'Sort Listings:Featured',
    nd: 'Sort Listings:Newest',
    pa: 'Sort Listings:Price (low)',
    pd: 'Sort Listings:Price (high)',
    ba: 'Sort Listings:Beds (least)',
    bd: 'Sort Listings:Beds (most)',
  };

  /**
   * Mapping of CtaSortSavedPropertyType to event action
   */
  protected mappingSortSavedProperty: DataKeyValueStringInterface = {
    '-save_date': 'Sort Listings:Recently saved',
    price: 'Sort Listings:Price (low)',
    '-price': 'Sort Listings:Price (high)',
    recent: 'Sort Listings:Recently posted',
  };

  /**
   * Mapping of CtaSortContactedPropertyType to event action
   */
  protected mappingSortContactedProperty: DataKeyValueStringInterface = {
    '-contact_date': 'Sort Listings:Recently contacted',
    price: 'Sort Listings:Price (low)',
    '-price': 'Sort Listings:Price (high)',
    recent: 'Sort Listings:Recently posted',
  };

  /**
   * Mapping of mappingSortMyNotesProperty to event action
   */
  protected mappingSortMyNotesProperty: DataKeyValueStringInterface = {
    '-modified_date': 'Sort Listings:Recently described',
    price: 'Sort Listings:Price (low)',
    '-price': 'Sort Listings:Price (high)',
    recent: 'Sort Listings:Recently posted',
  };

  /**
   * Mapping of CtaAuthenticationType to event action
   */
  protected mappingAuthentication: DataKeyValueStringInterface = {
    /**
     * When user wants to login (open login form)
     */
    'login-popup': 'Click:Log in Button',

    /**
     * When user wants to login from tooltip (open login form)
     */
    'login-popup-tooltip': 'Click:Log in Button',

    /**
     * When user wants to close login tooltip (close login form)
     */
    'login-popup-tooltip-close-button': 'Close:Save Property Pop Up',

    /**
     * When clicked on "Email alert" button
     */
    'email-alert-start-login': 'Signup:Start',

    /**
     * When clicked on "Create Alert" button in email alert modal window
     */
    'email-alert-finish-login': 'Signup:Finish',

    /**
     * When clicked on sign in with facebook
     */
    'start-signin-facebook': 'Start:Sign in with facebook',

    /**
     * When clicked on sign in with google
     */
    'start-signin-google': 'Start:Sign in with google',

    /**
     * When started reset password
     */
    'start-reset-password': 'Start:Password Reset',

    /**
     * When finished reset password
     */
    'finish-reset-password': 'Finish:Password Reset',

    /**
     * When user registration starts
     */
    'start-register': 'Start:Sign up with Email',

    /**
     * When user click on signin button from lead form
     */
    'login-popup-from-lead': 'Click:Sign in Button',

    /**
     * When user click on not now from lead form
     */
    'login-popup-not-now-button': 'Click:Not now Button',

    /**
     * When user click on close button from lead form
     */
    'login-popup-close-button': 'Click:Close Popup',
  };

  /**
   * Mapping of CtaSavedPropertiesType to event action
   */
  protected mappingSavedProperties: DataKeyValueStringInterface = {
    'start-finding': 'Click:Start finding',
  };

  /**
   * Mapping of Calculator events
   */
  protected mappingCalculatorEvents: DataKeyValueStringInterface = {
    /**
     * When user click on `Lebanon Audi Calculator` calculate button
     */
    'lebanon-audi-calculator-widget': 'Click Widget:Bank Audi',

    /**
     * When user click on `Sarouty Simulateur calculator` calculate button
     */
    'sarouty-simulateur-calculator-widget': 'Click Widget:Sarouty Loan Simulator',

    /**
     * When user click on `Esimsar Home loan calculator` calculate button
     */
    'esimsar-home-loan-calculator-widget': 'Click Widget:Saudi Home Loans',
  };

  /**
   * Mapping of CtaNotesPropertiesType to event action
   */
  protected mappingNotesProperties: DataKeyValueStringInterface = {
    /**
     * When user click on save note button
     */
    'save-property-note': 'Save:Property Note',

    /**
     * When user click on delete note button
     */
    'delete-property-note': 'Delete:Property Note',

    /**
     * When user logged out and click on save note button
     */
    'login-property-note': 'Click:Sign in Button',
  };

  /**
   * Constructor
   *
   * @param context
   */
  constructor(protected context: StatsContextInterface) {}

  /**
   * @inheritDoc
   */
  public getData(): GtmStatsDataEventCtaClickInterface {
    if (!this.context.local.cta) {
      throw new Error('Stats local context "cta" is not set.');
    }

    // CTA: download
    if (this.context.local.cta.download) {
      return this.getEvent('User Interaction', this.mappingDownload[this.context.local.cta.download]);
    }

    // CTA: client
    if (this.context.local.cta.client) {
      return this.getEvent('User Interaction', this.mappingClient[this.context.local.cta.client]);
    }

    // CTA: filter
    if (this.context.local.cta.filter) {
      // CTA: filter property
      if (this.context.local.cta.filter.property) {
        return this.getEvent(
          this.context.global.agent_page || this.context.global.broker_page ? 'Agents Section' : 'User Interaction',
          this.mappingFilterProperty[this.context.local.cta.filter.property]
        );
      }
    }

    // CTA: sort
    if (this.context.local.cta.sort) {
      // CTA: sort property
      if (this.context.local.cta.sort.property) {
        return this.getEvent(
          this.context.global.agent_page || this.context.global.broker_page ? 'Agents Section' : '',
          this.mappingSortProperty[this.context.local.cta.sort.property]
        );
      }

      // CTA: sort saved property
      if (this.context.local.cta.sort.savedProperty) {
        return this.getEvent(
          'User Interaction',
          this.mappingSortSavedProperty[this.context.local.cta.sort.savedProperty]
        );
      }

      // CTA: sort contacted property
      if (this.context.local.cta.sort.contactedProperty) {
        return this.getEvent(
          'User Interaction',
          this.mappingSortContactedProperty[this.context.local.cta.sort.contactedProperty]
        );
      }

      // CTA: sort myNotes property
      if (this.context.local.cta.sort.myNotesProperty) {
        return this.getEvent(
          'User Interaction',
          this.mappingSortMyNotesProperty[this.context.local.cta.sort.myNotesProperty]
        );
      }
    }

    // CTA: Notes event
    if (this.context.local.cta.notes) {
      if (this.context.local.cta.notes.name === 'login-property-note') {
        return this.getEvent('Userbox', this.mappingNotesProperties[this.context.local.cta.notes.name]);
      }
      return this.getEvent('User Interaction', this.mappingNotesProperties[this.context.local.cta.notes.name]);
    }

    // CTA: authentication
    if (this.context.local.cta.authentication) {
      // CTA: authentication close button
      if (this.context.local.cta.authentication === 'login-popup-tooltip-close-button') {
        return this.getEvent('User Interaction', this.mappingAuthentication[this.context.local.cta.authentication]);
      }

      if (
        this.context.local.cta.authentication === 'email-alert-start-login' ||
        this.context.local.cta.authentication === 'email-alert-finish-login'
      ) {
        return this.getEvent('Email Alert', this.mappingAuthentication[this.context.local.cta.authentication]);
      }

      return this.getEvent('Userbox', this.mappingAuthentication[this.context.local.cta.authentication]);
    }

    // CTA: saved properties
    if (this.context.local.cta.savedProperties) {
      return this.getEvent('User Interaction', this.mappingSavedProperties[this.context.local.cta.savedProperties]);
    }

    // CTA: custom event
    if (this.context.local.cta.event) {
      if (this.context.local.cta.event.name && this.context.local.cta.event.action) {
        return this.getEvent(this.context.local.cta.event.name, this.context.local.cta.event.action);
      }
    }

    // CTA: Calculator event
    if (this.context.local.cta.calculator) {
      return this.getEvent('User Interaction', this.mappingCalculatorEvents[this.context.local.cta.calculator.name]);
    }

    throw new Error('GtmStatsAdapterEventCtaClick is unable to define the event.');
  }

  /**
   * Get sanitized event
   *
   * @param name
   * @param action
   */
  private getEvent(name: string, action: string): GtmStatsDataEventCtaClickInterface {
    const event: GtmStatsDataEventCtaClickInterface = {
      event: name,
      eventAction: action,
      eventLabel: new GtmStatsAdapterList(this.context).getData(),
    };

    if (!event.event) {
      throw new Error('GtmStatsAdapterEventCtaClick is unable to define the event name.');
    }

    if (!event.eventAction) {
      throw new Error('GtmStatsAdapterEventCtaClick is unable to define the event action.');
    }

    return event;
  }
}
